import React, { useState, useEffect } from "react";
import { Button } from "@heroui/button";
import { Input } from "@heroui/input";
import { Switch } from "@heroui/switch";

import { Category } from "../../types/menu";

interface CategoryFormModalProps {
  category: Category | null;
  onSave: (categoryData: any) => Promise<void>;
  onCancel: () => void;
  loading: boolean;
}

const CategoryFormModal: React.FC<CategoryFormModalProps> = ({
  category,
  onSave,
  onCancel,
  loading,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    position: "",
    hidden: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form with existing category data if editing
  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name,
        description: category.description || "",
        position: category.position !== null ? String(category.position) : "",
        hidden: category.hidden,
      });
    }
  }, [category]);

  // Handle form input changes
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };

        delete newErrors[name];

        return newErrors;
      });
    }
  };

  // Handle switch toggle for hidden flag
  const handleHiddenChange = (checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      hidden: checked,
    }));
  };

  // Form validation
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Category name is required";
    }

    if (formData.position && !/^\d+$/.test(formData.position)) {
      newErrors.position = "Position must be a number";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Prepare data for submission
    const categoryData = {
      name: formData.name.trim(),
      description: formData.description.trim() || null,
      position: formData.position ? parseInt(formData.position) : null,
      hidden: formData.hidden,
    };

    await onSave(categoryData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
            {category ? "Edit Category" : "Add Category"}
          </h3>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-4">
            <div>
              <Input
                isRequired
                errorMessage={errors.name}
                isInvalid={!!errors.name}
                label="Category Name"
                name="name"
                placeholder="Enter category name"
                value={formData.name}
                onChange={handleChange}
              />
            </div>

            <div>
              <Input
                as="textarea"
                label="Description (Optional)"
                name="description"
                placeholder="Enter category description"
                rows={3}
                value={formData.description}
                onChange={handleChange}
              />
            </div>

            <div>
              <Input
                errorMessage={errors.position}
                isInvalid={!!errors.position}
                label="Display Position (Optional)"
                name="position"
                placeholder="Enter display order (lower numbers appear first)"
                type="number"
                value={formData.position}
                onChange={handleChange}
              />
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Lower numbers will appear first in the menu
              </p>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {formData.hidden
                  ? "Category is hidden from menu"
                  : "Category is visible in menu"}
              </span>
              <Switch
                aria-label="Hide category"
                isSelected={formData.hidden}
                size="sm"
                onValueChange={handleHiddenChange}
              />
            </div>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-2">
            <Button disabled={loading} variant="light" onPress={onCancel}>
              Cancel
            </Button>
            <Button color="primary" isLoading={loading} type="submit">
              {category ? "Update Category" : "Create Category"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CategoryFormModal;
